import { useState } from "react";
import { User, Phone, Trash2 } from "lucide-react";
import { usePopup } from "@/components/AppPopups";
import { Modal } from "./Modal";
import { scheduleType, toMidnight } from "../utils/calendarHelpers";
import { fmtDate } from "@/lib/format";

export function EntryDetailModal({ entry, onClose, onDelete }) {
  const popup = usePopup();
  const [loading, setLoading] = useState(false);
  const isBooking = scheduleType(entry) === "booking";
  const isCampaignBooking = String(entry.id || "").startsWith("campaign:");

  const start = toMidnight(entry.start_date);
  const end = toMidnight(entry.end_date);
  const days = Math.round((end - start) / 86400000) + 1;

  async function handleDelete() {
    const ok = await popup.confirm({
      title: `Remove this ${isBooking ? "booking" : "maintenance window"}?`,
      description: "This calendar entry will be removed from the hoarding schedule.",
      confirmText: "Remove Entry",
      tone: "danger",
    });
    if (!ok) return;
    setLoading(true);
    try {
      await onDelete(entry.id);
      onClose();
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      title={isBooking ? "Booking Details" : "Maintenance Window"}
      onClose={onClose}
      accent={isBooking ? "emerald" : "blue"}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className={`hx-status-pill ${isBooking ? "tone-emerald" : "tone-blue"}`}>
            {isBooking ? "Booking" : "Maintenance"}
          </span>
          <span className="text-xs text-slate-500 ml-1">{days} day{days !== 1 ? "s" : ""}</span>
        </div>

        {isBooking && entry.client_name && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <User size={15} className="text-slate-400 flex-shrink-0" />
              <span className="text-sm font-medium text-slate-900">{entry.client_name}</span>
            </div>
            {entry.client_mobile && (
              <div className="flex items-center gap-3">
                <Phone size={15} className="text-slate-400 flex-shrink-0" />
                <span className="text-sm font-mono text-slate-700">{entry.client_mobile}</span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 p-4 rounded-lg" style={{ background: "var(--hx-bg)" }}>
          <div>
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Start</div>
            <div className="text-sm font-mono font-semibold text-slate-900 mt-1">{fmtDate(entry.start_date)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">End</div>
            <div className="text-sm font-mono font-semibold text-slate-900 mt-1">{fmtDate(entry.end_date)}</div>
          </div>
        </div>

        {entry.notes && (
          <div className="p-3 rounded-lg border text-sm text-slate-600 leading-relaxed" style={{ background: "var(--hx-bg)", borderColor: "var(--hx-border)" }}>
            {entry.notes}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="hx-secondary-action flex-1">Close</button>
          {!isCampaignBooking && (
            <button
              onClick={handleDelete}
              disabled={loading}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-700 disabled:opacity-50"
            >
              <Trash2 size={14} />
              {loading ? "Removing…" : "Remove"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
